<template>
  <transition name="consent-slide">
    <div v-if="visible" class="cookie-consent-overlay">
      <div class="cookie-consent-banner glass-container steel-accent">
        <!-- Corner accents -->
        <div class="corner-accent top-left"></div>
        <div class="corner-accent top-right"></div>
        <div class="corner-accent bottom-left"></div>
        <div class="corner-accent bottom-right"></div>

        <div class="consent-content">
          <div class="consent-text">
            <h3 class="consent-title">Cookie Preferences</h3>
            <p class="consent-description">
              We use cookies to enhance your browsing experience and analyze site traffic.
              By clicking "Accept," you consent to our use of cookies in accordance with our privacy policy.
            </p>
          </div>
          <div class="consent-actions">
            <button class="consent-btn consent-btn-reject" @click="reject">
              Reject
            </button>
            <button class="consent-btn consent-btn-accept" @click="accept">
              Accept
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'CookieConsent',
  data() {
    return {
      visible: false
    }
  },
  mounted() {
    if (!this.hasConsented()) {
      this.visible = true
    }
  },
  methods: {
    hasConsented() {
      // Check for acceptance cookie
      if (document.cookie.split('; ').some(c => c.startsWith('ste_cookie_consent='))) {
        return true
      }
      // Check for session-level rejection
      if (sessionStorage.getItem('ste_cookie_rejected') === '1') {
        return true
      }
      return false
    },
    accept() {
      // Set a cookie that expires in 1 year
      const expires = new Date()
      expires.setFullYear(expires.getFullYear() + 1)
      document.cookie = `ste_cookie_consent=accepted; expires=${expires.toUTCString()}; path=/; SameSite=Lax`
      this.visible = false
    },
    reject() {
      // Store rejection in sessionStorage only — no cookie
      sessionStorage.setItem('ste_cookie_rejected', '1')
      this.visible = false
    }
  }
}
</script>

<style scoped>
.cookie-consent-overlay {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  padding: 1rem;
  pointer-events: none;
}

.cookie-consent-banner {
  position: relative;
  max-width: 960px;
  margin: 0 auto;
  padding: 1.5rem 2rem;
  pointer-events: auto;
  overflow: hidden;
  background: rgba(29, 30, 38, 0.92);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.75rem;
  box-shadow:
    0 -4px 30px rgba(0, 0, 0, 0.4),
    0 0 60px rgba(143, 0, 0, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

/* Corner accent decorations */
.corner-accent {
  position: absolute;
  width: 16px;
  height: 16px;
  pointer-events: none;
}
.corner-accent::before,
.corner-accent::after {
  content: '';
  position: absolute;
  background: linear-gradient(90deg, rgba(143, 0, 0, 0.8), rgba(183, 185, 186, 0.4));
}
.corner-accent::before {
  width: 16px;
  height: 2px;
}
.corner-accent::after {
  width: 2px;
  height: 16px;
}
.top-left { top: 0; left: 0; }
.top-left::before { top: 0; left: 0; }
.top-left::after { top: 0; left: 0; }
.top-right { top: 0; right: 0; }
.top-right::before { top: 0; right: 0; }
.top-right::after { top: 0; right: 0; }
.bottom-left { bottom: 0; left: 0; }
.bottom-left::before { bottom: 0; left: 0; }
.bottom-left::after { bottom: 0; left: 0; }
.bottom-right { bottom: 0; right: 0; }
.bottom-right::before { bottom: 0; right: 0; }
.bottom-right::after { bottom: 0; right: 0; }

.consent-content {
  display: flex;
  align-items: center;
  gap: 1.25rem;
}

.consent-text {
  flex: 1;
  text-align: left;
}

.consent-title {
  font-family: 'PT Sans', sans-serif;
  font-size: 1.25rem;
  font-weight: bold;
  color: white;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
  margin: 0 0 0.35rem 0;
}

.consent-description {
  font-family: 'PT Sans', sans-serif;
  font-size: 0.8rem;
  line-height: 1.5;
  margin: 0;
  color: rgba(183, 185, 186, 0.85);
}

.consent-actions {
  display: flex;
  gap: 0.75rem;
  flex-shrink: 0;
}

.consent-btn {
  font-family: 'PT Sans', sans-serif;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 0.6rem 1.5rem;
  border-radius: 0.35rem;
  border: none;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.25, 1, 0.5, 1);
  white-space: nowrap;
}

.consent-btn-reject {
  background: transparent;
  color: rgba(183, 185, 186, 0.7);
  border: 1px solid rgba(183, 185, 186, 0.2);
}
.consent-btn-reject:hover {
  color: rgba(183, 185, 186, 1);
  border-color: rgba(183, 185, 186, 0.4);
  background: rgba(183, 185, 186, 0.08);
}

.consent-btn-accept {
  background: linear-gradient(135deg, #8f0000, #a31a1a);
  color: #f0f0f0;
  box-shadow: 0 2px 12px rgba(143, 0, 0, 0.3);
}
.consent-btn-accept:hover {
  background: linear-gradient(135deg, #a31a1a, #bf2020);
  box-shadow: 0 4px 20px rgba(143, 0, 0, 0.45);
  transform: translateY(-1px);
}
.consent-btn-accept:active {
  transform: translateY(0);
}

/* Responsive: stack vertically on small screens */
@media (max-width: 640px) {
  .cookie-consent-overlay {
    padding: 0.75rem;
  }
  .cookie-consent-banner {
    padding: 1.25rem;
  }
  .consent-content {
    flex-direction: column;
    text-align: center;
    gap: 1rem;
  }
  .consent-text {
    text-align: center;
  }
  .consent-actions {
    width: 100%;
    justify-content: center;
  }
}

/* Slide-up transition */
.consent-slide-enter-active {
  transition: all 0.5s cubic-bezier(0.22, 1, 0.36, 1);
}
.consent-slide-leave-active {
  transition: all 0.35s cubic-bezier(0.55, 0, 1, 0.45);
}
.consent-slide-enter-from,
.consent-slide-leave-to {
  opacity: 0;
  transform: translateY(100%);
}
</style>
